/**
 * Policy Attributes Viewer Component
 * Displays XACML policy attributes in a structured, readable format
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight, User, FileText, Zap, Globe } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import type {
  ParsedXacmlAttributes,
  ParsedXacmlAttribute,
  XacmlCategory,
} from '@/types/policy'
import {
  getCategoryDisplayName,
  getDataTypeName,
  formatAttributeValue,
  hasAttributes,
} from '@/lib/xacml-parser'
import { cn } from '@/lib/utils'

/**
 * Props for PolicyAttributesViewer
 */
interface PolicyAttributesViewerProps {
  attributes: ParsedXacmlAttributes
  title?: string
  description?: string
  className?: string
  defaultExpanded?: boolean
}

/**
 * Get icon component for category
 */
function CategoryIcon({
  category,
  className,
}: {
  category: XacmlCategory
  className?: string
}) {
  const iconClass = cn('h-4 w-4', className)

  switch (category) {
    case 'subject':
      return <User className={iconClass} />
    case 'resource':
      return <FileText className={iconClass} />
    case 'action':
      return <Zap className={iconClass} />
    case 'environment':
      return <Globe className={iconClass} />
    default:
      return null
  }
}

/**
 * Single attribute item display
 */
function AttributeItem({ attribute }: { attribute: ParsedXacmlAttribute }) {
  const dataTypeName = getDataTypeName(attribute.dataType)

  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border/50 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm font-medium text-foreground">
            {attribute.attributeId}
          </span>
          <Badge variant="outline" className="text-xs">
            {dataTypeName}
          </Badge>
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {attribute.values.map((value, index) => (
            <Badge key={index} variant="secondary" className="font-mono text-xs">
              {formatAttributeValue(value, attribute.dataType)}
            </Badge>
          ))}
          {attribute.values.length === 0 && (
            <span className="text-xs text-muted-foreground italic">No values</span>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Category section (collapsible)
 */
function AttributeCategory({
  category,
  attributes,
  defaultOpen = true,
}: {
  category: XacmlCategory
  attributes: ParsedXacmlAttribute[]
  defaultOpen?: boolean
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  if (attributes.length === 0) {
    return null
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between px-3 py-2 h-auto hover:bg-muted/50"
        >
          <div className="flex items-center gap-2">
            <CategoryIcon category={category} className="text-muted-foreground" />
            <span className="font-medium">{getCategoryDisplayName(category)} Attributes</span>
            <Badge variant="secondary" className="ml-1">
              {attributes.length}
            </Badge>
          </div>
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3 pt-1">
          <div className="rounded-md border bg-muted/30 p-3">
            {attributes.map((attr, index) => (
              <AttributeItem key={`${attr.attributeId}-${index}`} attribute={attr} />
            ))}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

/**
 * Main Policy Attributes Viewer component
 */
export function PolicyAttributesViewer({
  attributes,
  title = 'Policy Target',
  description = 'Conditions that determine when this policy applies',
  className,
  defaultExpanded = true,
}: PolicyAttributesViewerProps) {
  const categories: XacmlCategory[] = ['subject', 'resource', 'action', 'environment']

  if (!hasAttributes(attributes)) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground italic">
            No target attributes defined. This policy applies to all requests.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{title}</CardTitle>
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {categories.map((category) => (
            <AttributeCategory
              key={category}
              category={category}
              attributes={attributes[category]}
              defaultOpen={defaultExpanded}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Compact inline attributes display (for use in rule cards)
 */
export function AttributesInline({
  attributes,
  className,
}: {
  attributes: ParsedXacmlAttributes
  className?: string
}) {
  const categories: XacmlCategory[] = ['subject', 'resource', 'action', 'environment']

  if (!hasAttributes(attributes)) {
    return (
      <span className={cn('text-sm text-muted-foreground italic', className)}>
        No specific target
      </span>
    )
  }

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {categories.map((category) => {
        const attrs = attributes[category]
        if (attrs.length === 0) return null

        return attrs.map((attr, index) => (
          <div
            key={`${category}-${attr.attributeId}-${index}`}
            className="flex items-center gap-1"
          >
            <CategoryIcon category={category} className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs font-mono">{attr.attributeId}:</span>
            {attr.values.map((value, vIndex) => (
              <Badge key={vIndex} variant="outline" className="text-xs font-mono">
                {formatAttributeValue(value, attr.dataType)}
              </Badge>
            ))}
          </div>
        ))
      })}
    </div>
  )
}

export default PolicyAttributesViewer
